/**
 * Resume-from-position banner.
 *
 * Read on player mount: if `zap.pos.v2.<key>` > 30s, show banner with two
 * buttons: "Fortsetzen" (jumps to saved position) and "Von Anfang".
 *
 * Writes on timeupdate (debounced 5s) and on destroy. Clears the key when
 * within 30s of end ("watched").
 */
import { fmtTime } from '../core/utils.js';

// v1 could contain raw MPEG-TS/Blu-ray PTS values (commonly +01:00:00) from
// before the native engine normalized container timelines. Do not reinterpret
// those absolute timestamps as zero-based resume positions after that fix.
const RESUME_PREFIX = 'zap.pos.v2.';

function key(rawPath) {
  const k = rawPath || '';
  return RESUME_PREFIX + (k.length > 60 ? k.slice(-60) : k);
}

export function readResumePos(rawPath) {
  try { return parseFloat(localStorage.getItem(key(rawPath)) || '0') || 0; }
  catch { return 0; }
}

export function writeResumePos(rawPath, t, dur) {
  try {
    if (dur > 0 && (dur - t) < 30) localStorage.removeItem(key(rawPath));
    else if (t > 10) localStorage.setItem(key(rawPath), String(t));
  } catch { /* noop */ }
}

export function clearResumePos(rawPath) {
  try { localStorage.removeItem(key(rawPath)); } catch { /* noop */ }
}

export function createResumeBanner(savedPos, onResume, onSkip) {
  if (!(savedPos > 30)) return null;
  const el = document.createElement('div');
  el.className = 'zap-pe-resume';
  el.innerHTML = `
    <span>Weiter ab <strong>${fmtTime(savedPos)}</strong>?</span>
    <button type="button" class="primary" data-act="resume">Fortsetzen</button>
    <button type="button" data-act="skip">Von Anfang</button>
  `;
  el.querySelector('[data-act="resume"]').addEventListener('click', () => { el.remove(); onResume(); });
  el.querySelector('[data-act="skip"]')  .addEventListener('click', () => { el.remove(); onSkip();   });
  return el;
}

/** Attach a debounced timeupdate writer (5s interval). Returns a detach fn. */
export function attachResumeWriter(video, rawPath) {
  let t = null;
  const onTime = () => {
    if (t) return;
    t = setTimeout(() => {
      t = null;
      writeResumePos(rawPath, video.currentTime || 0, video.duration || 0);
    }, 5000);
  };
  video.addEventListener('timeupdate', onTime);
  return () => {
    video.removeEventListener('timeupdate', onTime);
    if (t) clearTimeout(t);
    // Final write on detach — capture last position
    writeResumePos(rawPath, video.currentTime || 0, video.duration || 0);
  };
}
